<template>
  <q-dialog
    v-model="dialogOpen"
    persistent
    maximized
    transition-show="slide-up"
    transition-hide="slide-down"
  >
    <q-card class="documentation-dialog">
      <q-card-section class="bg-primary text-white">
        <div class="row items-center">
          <div class="col">
            <div class="text-h6">
              Document {{ equipmentName }}
            </div>
            <div class="text-caption">
              {{ brickClass || equipmentType || 'Equipment' }}
            </div>
          </div>
          <div class="col-auto">
            <q-circular-progress
              :value="completionPercentage"
              size="50px"
              :thickness="0.2"
              :color="statusColor"
              track-color="grey-3"
              class="q-mr-md"
            >
              <div class="text-white text-bold">{{ completionPercentage }}%</div>
            </q-circular-progress>
            <q-chip
              :color="statusColor"
              text-color="white"
              :icon="statusIcon"
            >
              {{ documentationStatus }}
            </q-chip>
          </div>
          <q-btn
            flat
            round
            dense
            icon="close"
            aria-label="Close documentation dialog"
            @click="close"
          />
        </div>
      </q-card-section>

      <q-card-section class="q-pa-none">
        <q-tabs
          v-model="activeTab"
          class="text-primary"
          align="left"
          indicator-color="primary"
        >
          <q-tab
            name="airflow"
            label="Airflow Data"
            icon="air">
            <q-badge v-if="getCompletionStatus('airflow')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="function" label="Function" icon="description">
            <q-badge v-if="getCompletionStatus('function')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="program" label="Control Program" icon="code">
            <q-badge v-if="getCompletionStatus('program')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="specifications" label="Specifications" icon="assessment">
            <q-badge v-if="getCompletionStatus('specifications')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="technical" label="Technical" icon="engineering">
            <q-badge v-if="getCompletionStatus('technical')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="maintenance" label="Maintenance" icon="build">
            <q-badge v-if="getCompletionStatus('maintenance')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
          <q-tab name="documents" label="Documents" icon="attach_file">
            <q-badge v-if="documentation.attached_documents?.length > 0" color="primary" floating>
              {{ documentation.attached_documents.length }}
            </q-badge>
          </q-tab>
          <q-tab name="brick" label="BRICK" icon="schema">
            <q-badge v-if="getCompletionStatus('brick')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
        </q-tabs>

        <q-separator />

        <q-tab-panels v-model="activeTab" animated class="documentation-panels">
          <!-- Airflow Tab - Keep original for now (complex with Sankey diagrams) -->
          <q-tab-panel name="airflow" class="q-pa-md">
            <div class="text-caption text-grey-7 q-mb-md">
              ⚠️ Airflow tab: Full integration pending (requires Sankey diagram migration)
            </div>
            <AirflowDocumentationTab
              v-model="documentation"
              :equipment-name="equipmentName"
              @update:airflow-field="handleAirflowFieldUpdate"
              @refresh-diagram="refreshAirflowDiagram"
            />
          </q-tab-panel>

          <!-- Function Tab - INTEGRATED ✅ -->
          <q-tab-panel name="function" class="q-pa-md">
            <FunctionDocumentationTab
              v-model="documentation.function_description"
              @update:purpose="documentation.function_description.purpose = $event"
              @update:operation-modes="documentation.function_description.operation_modes = $event"
              @update:control-strategy="documentation.function_description.control_strategy = $event"
            />
          </q-tab-panel>

          <!-- Program Tab - INTEGRATED ✅ -->
          <q-tab-panel name="program" class="q-pa-md">
            <ProgramDocumentationTab
              v-model="documentation"
              @update:program-schedule="handleProgramScheduleUpdate"
            />
          </q-tab-panel>

          <!-- Specifications Tab - INTEGRATED ✅ -->
          <q-tab-panel name="specifications" class="q-pa-md">
            <SpecificationsDocumentationTab
              v-model="documentation"
              @update:specification="handleSpecificationUpdate"
            />
          </q-tab-panel>

          <!-- Technical Tab - INTEGRATED ✅ -->
          <q-tab-panel name="technical" class="q-pa-md">
            <TechnicalDocumentationTab
              v-model="documentation"
              @update:technical-spec="handleTechnicalSpecUpdate"
            />
          </q-tab-panel>

          <!-- Maintenance Tab - INTEGRATED ✅ -->
          <q-tab-panel name="maintenance" class="q-pa-md">
            <MaintenanceDocumentationTab
              v-model="documentation"
              @update:maintenance-notes="documentation.maintenance_notes = $event"
            />
          </q-tab-panel>

          <!-- Documents Tab - INTEGRATED ✅ -->
          <q-tab-panel name="documents" class="q-pa-md">
            <DocumentsDocumentationTab
              v-model="documentation"
              @upload-document="handleDocumentUpload"
              @download-document="handleDocumentDownload"
              @remove-document="handleDocumentRemove"
            />
          </q-tab-panel>

          <!-- BRICK Tab - Keep original for now (complex TTL generation) -->
          <q-tab-panel name="brick" class="q-pa-md">
            <div class="text-caption text-grey-7 q-mb-md">
              ⚠️ BRICK tab: Full integration pending (requires complete TTL generation migration)
            </div>
            <BrickDocumentationTab
              v-model="documentation"
              :equipment-details="equipmentDetails"
              :ttl-preview="ttlPreview"
              @add-relationship="showAddRelationshipDialog = true"
              @remove-relationship="removeRelationship"
              @refresh-ttl="updateTtlPreview"
            />
          </q-tab-panel>
        </q-tab-panels>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right" class="q-pa-md">
        <q-btn
          flat
          label="Cancel"
          color="grey"
          @click="close"
        />
        <q-btn
          v-if="activeTab !== 'airflow'"
          unelevated
          label="Save Documentation"
          color="primary"
          icon="save"
          :loading="saving"
          @click="saveDocumentation"
        />
      </q-card-actions>
    </q-card>

    <!-- Add Relationship Dialog (preserved from original) -->
    <q-dialog v-model="showAddRelationshipDialog">
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Add BRICK Relationship</div>
        </q-card-section>

        <q-card-section>
          <q-select
            v-model="newRelationship.predicate"
            label="Predicate"
            :options="brickPredicates"
            outlined
            dense
            class="q-mb-md"
          />

          <q-select
            v-model="newRelationship.selectedEquipment"
            label="Select Related Equipment"
            :options="siteEquipmentList"
            option-label="label"
            option-value="value"
            outlined
            dense
            emit-value
            map-options
            class="q-mb-md"
            @update:model-value="onRelationshipEquipmentSelected"
          >
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  No equipment available
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <q-input
            v-model="newRelationship.object"
            label="Object URI (Auto-generated)"
            outlined
            dense
            readonly
            :hint="newRelationship.selectedEquipment ? 'URI generated from selected equipment' : 'Select equipment above'"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="showAddRelationshipDialog = false" />
          <q-btn
            flat
            label="Add"
            color="primary"
            :disable="!newRelationship.predicate || !newRelationship.object"
            @click="addRelationship"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
/**
 * Documentation Dialog - REFACTORED VERSION
 *
 * Phase 3 Integration: Uses extracted tab components and composables
 *
 * Integrated Tabs (6/8):
 * ✅ FunctionDocumentationTab
 * ✅ ProgramDocumentationTab
 * ✅ SpecificationsDocumentationTab
 * ✅ TechnicalDocumentationTab
 * ✅ MaintenanceDocumentationTab
 * ✅ DocumentsDocumentationTab
 *
 * Pending Full Integration (2/8):
 * ⚠️ AirflowDocumentationTab (requires Sankey diagram migration)
 * ⚠️ BrickDocumentationTab (requires complete TTL generation migration)
 */

  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '../../services/api';

  // Import tab components
  import FunctionDocumentationTab from './documentation-tabs/FunctionDocumentationTab.vue';
  import TechnicalDocumentationTab from './documentation-tabs/TechnicalDocumentationTab.vue';
  import ProgramDocumentationTab from './documentation-tabs/ProgramDocumentationTab.vue';
  import SpecificationsDocumentationTab from './documentation-tabs/SpecificationsDocumentationTab.vue';
  import MaintenanceDocumentationTab from './documentation-tabs/MaintenanceDocumentationTab.vue';
  import DocumentsDocumentationTab from './documentation-tabs/DocumentsDocumentationTab.vue';
  import AirflowDocumentationTab from './documentation-tabs/AirflowDocumentationTab.vue';
  import BrickDocumentationTab from './documentation-tabs/BrickDocumentationTab.vue';

  // Import legacy component (still needed for some tabs)
  import PointUnitSelector from './PointUnitSelector.vue';

  // Import composables
  import { useDocumentationForm } from '../../composables/brick-schema/documentation/useDocumentationForm';
  import { useBrickSchemaVisualization } from '../../composables/brick-schema/documentation/useBrickSchemaVisualization';

  const props = defineProps({
    modelValue: Boolean,
    equipmentId: Number,
    equipmentType: String,
    equipmentName: String,
    brickClass: String,
    siteId: Number,
  });

  const emit = defineEmits(['update:modelValue', 'documented', 'navigate-to-airflow']);

  const $q = useQuasar();

  const dialogOpen = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  const activeTab = ref('function'); // Start with function tab since it's fully integrated
  const showAddRelationshipDialog = ref(false);
  const siteEquipmentList = ref([]);
  const equipmentDetails = ref(null);

  // Initialize documentation form composable
  const {
    documentation,
    saving,
    loading,
    hasUnsavedChanges,
    loadDocumentation,
    saveDocumentation: saveDoc,
    updateTechnicalSpec,
    updateProgramSchedule,
    updateSpecification,
    updateMaintenanceNotes,
    addDocument,
    removeDocument: removeDoc,
    addBrickRelationship,
    removeBrickRelationship,
  } = useDocumentationForm({
    equipmentId: props.equipmentId,
    equipmentType: props.equipmentType,
    siteId: props.siteId,
    onSaveSuccess: (response) => {
      emit('documented', response);
      close();
    },
    onSaveError: (error) => {
      console.error('Save error:', error);
    },
    notifySuccess: (message) => {
      $q.notify({
        type: 'positive',
        message,
        position: 'top',
      });
    },
    notifyError: (message) => {
      $q.notify({
        type: 'negative',
        message,
        position: 'top',
      });
    },
    autoSave: false, // Manual save for now
  });

  // Initialize BRICK schema visualization composable
  const {
    ttlPreview,
    generating,
    updateTtlPreview,
    getRelationshipIcon,
  } = useBrickSchemaVisualization({
    equipmentId: props.equipmentId,
    siteId: props.siteId,
    equipmentName: props.equipmentName,
    brickClass: props.brickClass,
    documentation,
    equipmentDetails,
  });

  // BRICK relationships management
  const newRelationship = ref({
    predicate: '',
    object: '',
    selectedEquipment: null,
  });

  const brickPredicates = [
    'brick:feeds',
    'brick:isFedBy',
    'brick:hasPoint',
    'brick:isPartOf',
    'brick:hasPart',
    'brick:hasLocation',
    'brick:controls',
    'brick:isControlledBy',
    'brick:meters',
    'brick:isMeteredBy',
  ];

  // Event handlers for tab components
  function handleTechnicalSpecUpdate({ field, value }) {
    updateTechnicalSpec(field, value);
  }

  function handleProgramScheduleUpdate({ field, value }) {
    updateProgramSchedule(field, value);
  }

  function handleSpecificationUpdate({ field, value }) {
    updateSpecification(field, value);
  }

  function handleAirflowFieldUpdate({ field, value }) {
    documentation.value.airflow_data[field] = value;
  }

  function handleDocumentUpload(documentData) {
    addDocument(documentData);
    $q.notify({
      type: 'positive',
      message: 'Document added',
      position: 'top',
    });
  }

  function handleDocumentDownload(doc) {
    // Implement document download
  }

  function handleDocumentRemove(index) {
    removeDoc(index);
    $q.notify({
      type: 'positive',
      message: 'Document removed',
      position: 'top',
    });
  }

  function refreshAirflowDiagram() {
    // Placeholder for airflow diagram refresh
  }

  function addRelationship() {
    if (!newRelationship.value.predicate || !newRelationship.value.object) {
      return;
    }

    addBrickRelationship({
      predicate: newRelationship.value.predicate,
      object: newRelationship.value.object,
    });

    // Reset form
    newRelationship.value = {
      predicate: '',
      object: '',
      selectedEquipment: null,
    };

    showAddRelationshipDialog.value = false;
    updateTtlPreview();
  }

  function removeRelationship(index) {
    removeBrickRelationship(index);
    updateTtlPreview();
  }

  function onRelationshipEquipmentSelected(equipmentId) {
    if (!equipmentId) {
      newRelationship.value.object = '';
      return;
    }

    const selectedEquipment = siteEquipmentList.value.find((eq) => eq.value === equipmentId);
    if (selectedEquipment) {
      newRelationship.value.object = selectedEquipment.brick_uri || `equipment:${selectedEquipment.name}`;
    }
  }

  async function loadSiteEquipment() {
    if (!props.siteId) {return;}

    try {
      const response = await api.get(`/sites/${props.siteId}/equipment`);
      if (response && Array.isArray(response)) {
        siteEquipmentList.value = response.map((eq) => ({
          label: `${eq.name} (${eq.brick_class || eq.type || 'Equipment'})`,
          value: eq.id,
          brick_uri: eq.brick_class ?
            `equipment:${eq.name.replace(/\s+/g, '_')}` :
            `equipment:${eq.name.replace(/\s+/g, '_')}_${eq.id}`,
          name: eq.name,
          type: eq.type,
          brick_class: eq.brick_class,
        }));
      }
    } catch (error) {
      console.error('Failed to load site equipment:', error);
    }
  }

  async function loadEquipmentDetails() {
    if (!props.equipmentId) {return;}

    try {
      const response = await api.get(`/equipment/${props.equipmentId}`, {
        params: { with: 'zone_sections.feeds_equipment.compound_components' },
      });
      if (response) {
        equipmentDetails.value = response;
      }
    } catch (error) {
      console.error('Failed to load equipment details:', error);
    }
  }

  function saveDocumentation() {
    saveDoc();
  }

  function close() {
    dialogOpen.value = false;
  }

  function getCompletionStatus(section) {
    switch(section) {
      case 'airflow':
        return documentation.value.airflow_data?.supply_flow ||
          documentation.value.airflow_data?.connected_equipment?.length > 0;
      case 'function':
        return documentation.value.function_description?.purpose?.length > 10;
      case 'program':
        return documentation.value.program_schedule?.weekday_start ||
          documentation.value.program_schedule?.sequence_of_operations;
      case 'specifications':
        return documentation.value.specifications?.design_cfm ||
          documentation.value.specifications?.measured_cfm;
      case 'technical':
        return documentation.value.technical_specs?.manufacturer ||
          documentation.value.technical_specs?.model;
      case 'maintenance':
        return documentation.value.maintenance_notes?.length > 10;
      case 'brick':
        return documentation.value.brick_relationships?.length > 0;
      default:
        return false;
    }
  }

  const completionPercentage = computed(() => {
    const sections = ['airflow', 'function', 'program', 'specifications', 'technical', 'maintenance', 'brick'];
    const completed = sections.filter((section) => getCompletionStatus(section)).length;
    return Math.round((completed / sections.length) * 100);
  });

  const documentationStatus = computed(() => {
    if (completionPercentage.value === 0) {return 'Not Started';}
    if (completionPercentage.value < 50) {return 'In Progress';}
    if (completionPercentage.value < 100) {return 'Nearly Complete';}
    return 'Complete';
  });

  const statusColor = computed(() => {
    if (completionPercentage.value === 0) {return 'grey';}
    if (completionPercentage.value < 50) {return 'orange';}
    if (completionPercentage.value < 100) {return 'blue';}
    return 'green';
  });

  const statusIcon = computed(() => {
    if (completionPercentage.value === 0) {return 'info';}
    if (completionPercentage.value < 100) {return 'pending';}
    return 'check_circle';
  });

  // Watch for equipment ID changes
  watch(() => props.equipmentId, async (newId) => {
    if (newId) {
      await loadDocumentation();
      await loadEquipmentDetails();
      await loadSiteEquipment();
      await updateTtlPreview();
    }
  });

  onMounted(async () => {
    if (props.equipmentId) {
      await loadDocumentation();
      await loadEquipmentDetails();
      await loadSiteEquipment();
      await updateTtlPreview();
    }
  });
</script>

<style scoped>
.documentation-dialog {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.documentation-panels {
  flex: 1;
  overflow-y: auto;
}

.full-height {
  height: 100%;
}
</style>
